import { AlertTriangle, RefreshCw, ArrowLeftRight, ShieldAlert } from 'lucide-react';

/**
 * Modal som vises når backend returnerer 409 conflict (task #301, 2026-07-02).
 * Optimistisk låsing: brukeren har åpnet planen på tidspunkt X, men en annen
 * bruker har lagret endringer i mellomtiden. Brukeren må velge hvordan
 * konflikten skal løses.
 */

interface ConflictResolutionModalProps {
  /** Navn/e-post på siste redigerende bruker (kan være null hvis ukjent) */
  lastEditorName: string | null;
  /** ISO-timestamp for nåværende DB-versjon */
  currentUpdatedAt: string;
  /** ISO-timestamp for versjonen brukeren jobbet med */
  expectedUpdatedAt: string;
  /** Bruker vil overskrive andres endringer (send igjen med force=true) */
  onOverwrite: () => void;
  /** Bruker vil forkaste egne endringer og laste inn siste DB-versjon */
  onReload: () => void;
  /** URL som "Vis i ny fane" skal navigere til. Bruker <a target="_blank"> så
      popup-blocker ikke slår inn (task #313, 2026-07-02). */
  comparisonUrl: string;
  /** Lukk modalen (avbryter save-forsøket) */
  onCancel: () => void;
}

function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMin / 60);
  if (diffMin < 1) return 'akkurat nå';
  if (diffMin < 60) return `for ${diffMin} minutt${diffMin === 1 ? '' : 'er'} siden`;
  if (diffHours < 24) return `for ${diffHours} time${diffHours === 1 ? '' : 'r'} siden`;
  return new Date(iso).toLocaleString('nb-NO');
}

export default function ConflictResolutionModal({
  lastEditorName,
  currentUpdatedAt,
  expectedUpdatedAt,
  onOverwrite,
  onReload,
  comparisonUrl,
  onCancel,
}: ConflictResolutionModalProps) {
  const editorLabel = lastEditorName || 'En annen bruker';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full overflow-hidden">
        {/* Header */}
        <div className="bg-red-50 border-b border-red-200 p-4 flex items-start gap-3">
          <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={22} />
          <div>
            <h2 className="text-lg font-bold text-red-900">Konflikt: samtidig redigering</h2>
            <p className="text-sm text-red-800 mt-1">
              <strong>{editorLabel}</strong> lagret endringer på denne studieplanen{' '}
              {formatRelativeTime(currentUpdatedAt)} — etter at du åpnet den. Dine
              endringer kan overskrive deres. Velg hvordan du vil håndtere konflikten:
            </p>
          </div>
        </div>

        {/* Timestamps */}
        <div className="p-4 border-b border-gray-100 bg-gray-50 text-sm space-y-1">
          <div className="flex justify-between text-gray-700">
            <span>Du åpnet planen:</span>
            <strong>{new Date(expectedUpdatedAt).toLocaleString('nb-NO')}</strong>
          </div>
          <div className="flex justify-between text-gray-700">
            <span>Sist lagret i DB:</span>
            <strong>{new Date(currentUpdatedAt).toLocaleString('nb-NO')}</strong>
          </div>
        </div>

        {/* Choices */}
        <div className="p-4 space-y-3">
          <button
            type="button"
            onClick={onReload}
            className="w-full flex items-start gap-3 p-3 text-left border border-kf-blue-200 bg-kf-blue-50 rounded hover:bg-kf-blue-100 transition-colors"
          >
            <RefreshCw className="text-kf-blue-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <div className="font-semibold text-kf-blue-900">Last inn nyeste versjon</div>
              <div className="text-sm text-kf-blue-800">
                Forkast dine endringer og se hva {editorLabel.toLowerCase()} lagret.
                Ditt utkast beholdes i nettleseren så du kan gjenopprette senere hvis
                du vil.
              </div>
            </div>
          </button>

          {/* Task #313: bruk <a target="_blank"> for å unngå popup-blocker
              som slår inn på window.open. URL har ?compare=1 så StudyPlanForm
              hopper over draft-recovery-modal i den nye fanen. */}
          <a
            href={comparisonUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-start gap-3 p-3 text-left border border-gray-200 bg-white rounded hover:bg-gray-50 transition-colors no-underline"
          >
            <ArrowLeftRight className="text-gray-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <div className="font-semibold text-gray-900">Vis siste lagrede versjon i ny fane</div>
              <div className="text-sm text-gray-700">
                Åpne DB-versjonen ved siden av så du kan sammenligne før du avgjør.
                Denne fanen forblir uendret.
              </div>
            </div>
          </a>

          <button
            type="button"
            onClick={onOverwrite}
            className="w-full flex items-start gap-3 p-3 text-left border border-red-200 bg-red-50 rounded hover:bg-red-100 transition-colors"
          >
            <ShieldAlert className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <div className="font-semibold text-red-900">Overskriv med mine endringer</div>
              <div className="text-sm text-red-800">
                Ignorer {editorLabel.toLowerCase()} sine endringer og lagre din
                versjon som gjeldende. Denne handlingen er endelig — deres endringer
                vil ikke være mulig å hente frem.
              </div>
            </div>
          </button>
        </div>

        {/* Cancel */}
        <div className="border-t border-gray-200 p-3 bg-gray-50 flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
          >
            Avbryt lagring
          </button>
        </div>
      </div>
    </div>
  );
}
